import { prettyDateTime } from "@app/date";
import {
  cancelRunningOperation,
  createReadableStatus,
  selectResourceByIdAndType,
} from "@app/deploy";
import {
  type FreezerActivity,
  fetchActivityById,
  formatActivityType,
  idForTargetResourceType,
  selectFreezerActivityById,
} from "@app/freezer";
import { useDispatch, useLoader, useQuery, useSelector } from "@app/react";
import { activityDetailConfigUrl, activityDetailUrl } from "@app/routes";
import { capitalize } from "@app/string-utils";
import { useEffect, useMemo, useState } from "react";
import { Outlet, useParams } from "react-router-dom";
import {
  BAR_COLUMNS_CLASS,
  Banner,
  BarColumn,
  Box,
  ButtonOps,
  DetailPageHeaderView,
  IconCheckCircleAlt,
  IconX,
  LoadingSpinner,
  type TabItem,
} from "../shared";
import { TargetCellContent } from "../shared/activity-table/activity-columns";
import { AppSidebarLayout } from "./app-sidebar-layout";

function DeletedResourceBanner({ activity }: { activity: FreezerActivity }) {
  const resourceFound = useSelector((s) =>
    selectResourceByIdAndType(s, {
      resourceId: activity.primaryResourceId,
      resourceType: activity.primaryResourceType,
    }),
  );

  const resourceDeleted =
    resourceFound !== null &&
    "deletedAt" in resourceFound &&
    (resourceFound as { deletedAt?: string }).deletedAt;

  if (!resourceFound || !resourceDeleted) return null;

  const handleLabel =
    resourceFound != null &&
    "handle" in resourceFound &&
    resourceFound?.handle &&
    resourceFound?.deletedAt
      ? ` (${resourceFound.handle})`
      : "";

  const knownDate = prettyDateTime(
    (resourceFound as { deletedAt?: string })?.deletedAt,
  );

  return (
    <Banner className="my-2" variant="warning">
      The target resource{handleLabel} for this activity was deprovisioned
      {resourceFound?.deletedAt ? ` as of ${knownDate}.` : "."}
    </Banner>
  );
}

function Actor({ activity }: { activity: FreezerActivity }) {
  if (!activity.userName && !activity.userEmail) {
    return <>N/A</>;
  }
  return <span>{activity.userName || activity.userEmail}</span>;
}

export function WaitingBanner({ activity }: { activity: FreezerActivity }) {
  const dispatch = useDispatch();
  const operationId = activity.externalId ?? "";
  const cancelRunningOperationAction = useMemo(
    () => cancelRunningOperation({ id: operationId }),
    [operationId],
  );
  const cancelationLoader = useLoader(cancelRunningOperationAction);
  const [
    userRequestedOperationCancelation,
    setUserRequestedOperationCancelation,
  ] = useState(false);
  const [
    userRequestedOperationCancelationError,
    setUserRequestedOperationCancelationError,
  ] = useState("");

  useEffect(() => {
    if (activity.description?.cancelled) {
      setUserRequestedOperationCancelation(true);
    }
  }, [activity.description?.cancelled]);

  useEffect(() => {
    if (
      cancelationLoader.isSuccess ||
      cancelationLoader.meta?.alreadyCancelled
    ) {
      setUserRequestedOperationCancelation(true);
      setUserRequestedOperationCancelationError("");
      return;
    }
    if (cancelationLoader.isError) {
      setUserRequestedOperationCancelation(false);
      setUserRequestedOperationCancelationError(
        cancelationLoader.message || "Failed to cancel operation",
      );
    }
  }, [
    cancelationLoader.isSuccess,
    cancelationLoader.isError,
    cancelationLoader.message,
  ]);

  if (
    !activity.id ||
    (activity.status !== "queued" && activity.status !== "running")
  )
    return null;

  const title = activity.title || formatActivityType(activity.activityType);
  const hasOperationId = !!operationId;

  const requestOperationCancelation = () => {
    if (
      !hasOperationId ||
      userRequestedOperationCancelation ||
      cancelationLoader.isLoading
    )
      return;
    setUserRequestedOperationCancelationError("");
    dispatch(cancelRunningOperationAction);
  };

  return (
    <Banner className="mb-2" variant="progress" showIcon={false}>
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-3">
          <LoadingSpinner color="#364ECC" variant="base" />
          <span>
            {capitalize(activity.status)}: {title}
          </span>
        </div>
        {hasOperationId && (
          <div className="flex items-center gap-2">
            {userRequestedOperationCancelationError && (
              <span className="text-red-300 text-sm">
                {userRequestedOperationCancelationError}
              </span>
            )}
            <ButtonOps
              envId={activity.accountId || ""}
              variant="delete"
              size="sm"
              onClick={requestOperationCancelation}
              disabled={
                userRequestedOperationCancelation || cancelationLoader.isLoading
              }
            >
              <IconX variant="sm" color="white" className="mr-1" />
              {userRequestedOperationCancelation
                ? "Cancelation requested"
                : "Cancel Operation"}
            </ButtonOps>
          </div>
        )}
      </div>
    </Banner>
  );
}

function ActivityStatusText({ status }: { status: string }) {
  const succeeded = status === "succeeded";
  const failed = status === "failed" || status === "canceled";
  const icon = succeeded ? (
    <IconCheckCircleAlt variant="sm" color="currentColor" />
  ) : failed ? (
    <IconX variant="sm" color="currentColor" />
  ) : null;
  const colorClass = succeeded
    ? "text-forest"
    : failed
      ? "text-red"
      : "text-black-500";
  return (
    <div
      className={`flex items-center gap-1 text-sm font-semibold w-fit ${colorClass}`}
    >
      {icon}
      <span>
        {createReadableStatus(status as import("@app/types").OperationStatus)}
      </span>
    </div>
  );
}

export function ActivityHeader({ activity }: { activity: FreezerActivity }) {
  return (
    <>
      <WaitingBanner activity={activity} />
      <DeletedResourceBanner activity={activity} />
      <Box size="none">
        <div className={BAR_COLUMNS_CLASS}>
          <BarColumn
            icon={
              <img
                src="/resource-types/logo-activity.png"
                className="w-[28px] h-[28px] rounded"
                style={{ backgroundColor: "#343B41" }}
                aria-label="Activity"
              />
            }
            label="Activity Type"
          >
            {formatActivityType(activity.activityType)}
          </BarColumn>
          <BarColumn label="ID">{activity.id}</BarColumn>
          <BarColumn label="Target">
            <TargetCellContent activity={activity} />
          </BarColumn>
          <BarColumn label="Occurred At">
            {prettyDateTime(activity.occurredAt)}
          </BarColumn>
          <BarColumn label="Actor">
            <Actor activity={activity} />
          </BarColumn>
          <BarColumn label="Status">
            <ActivityStatusText status={activity.status} />
          </BarColumn>
        </div>
      </Box>
    </>
  );
}

const APP_DEPLOYMENT_ACTIVITY_TYPES = new Set(["app.deploy", "app.configure"]);

function ActivityPageHeader() {
  const { id = "" } = useParams();

  const activity = useSelector((s) => selectFreezerActivityById(s, { id }));
  const loader = useQuery(fetchActivityById({ id }));

  const appId =
    activity.primaryResourceType.toLowerCase() === "app"
      ? activity.primaryResourceId
      : (idForTargetResourceType(activity.targetResources, "app") ?? "");
  const isAppDeployment =
    APP_DEPLOYMENT_ACTIVITY_TYPES.has(activity.activityType) && appId !== "";

  const tabs: TabItem[] = isAppDeployment
    ? [
        { name: "Logs", href: activityDetailUrl(id), end: true },
        { name: "Configuration", href: activityDetailConfigUrl(id) },
      ]
    : [];

  return (
    <DetailPageHeaderView
      {...loader}
      breadcrumbs={[]}
      titleLabel="Activity ID"
      title={activity.id ? activity.id : "Loading..."}
      detailsBox={<ActivityHeader activity={activity} />}
      tabs={tabs}
    />
  );
}

export const ActivityDetailLayout = () => {
  return (
    <AppSidebarLayout header={<ActivityPageHeader />}>
      <Outlet />
    </AppSidebarLayout>
  );
};
